import React, { useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Box, AppBar, Toolbar, Typography, Tabs, Tab, Container, Alert, Button, IconButton, useTheme, useMediaQuery } from '@mui/material';
import SiteFooter from '../SiteFooter';
import SponsorBand from '../SponsorBand';
import InstallPrompt from '../InstallPrompt';
import { useAuth } from '../../contexts/AuthContext';
import { trackPageView } from '../../utils/trackPageView';
import { AdminPanelSettings as AdminIcon, CalendarMonth as CalendarIcon, MenuBook as DictionaryIcon, PersonAdd as SubscribeIcon, WhatsApp as WhatsAppIcon, Edit as EditIcon } from '@mui/icons-material';

const TABS = [
  { label: "Éphéméride du jour", path: '/aujourdhui', icon: <CalendarIcon /> },
  { label: 'Dictionnaire', path: '/dictionnaire', icon: <DictionaryIcon /> },
  { label: 'Éditeur', path: '/editeur', icon: <EditIcon /> },
  { label: "S'inscrire", path: '/subscribe', icon: <SubscribeIcon /> },
];

function PublicPortalLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  // En dessous de 600px, les libellés (ex. "Éphéméride du jour") ne tiennent pas dans
  // 1/4 de la largeur d'écran avec variant="fullWidth" : ça debordait/tronquait le
  // texte. On passe en onglets icone seule sur mobile plutot que de rogner le texte.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Statistiques de frequentation : une visite par changement de page, sauf pour l'equipe connectee.
  const { isAuthenticated, user } = useAuth();
  // Retour vers l'administration, reserve au super admin
  const canGoToAdmin = isAuthenticated && user?.role === 'super_admin';
  useEffect(() => {
    if (!isAuthenticated) trackPageView(location.pathname);
  }, [location.pathname, isAuthenticated]);

  const activeTab = TABS.some(t => t.path === location.pathname) ? location.pathname : false;

  // Hauteur réelle du bandeau collé, publiée en variable CSS (--portal-sticky-height)
  // pour que les éléments collés dessous (palette de l'éditeur) s'y accrochent
  // exactement, sur ordinateur comme sur téléphone, au lieu d'une valeur fixe.
  const rootRef = useRef(null);
  const stickyRef = useRef(null);
  useEffect(() => {
    const root = rootRef.current;
    const sticky = stickyRef.current;
    if (!root || !sticky || typeof ResizeObserver === 'undefined') return undefined;
    const update = () => root.style.setProperty('--portal-sticky-height', `${sticky.offsetHeight}px`);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(sticky);
    return () => observer.disconnect();
  }, []);

  // Sur mobile la bannière (5 lignes) mangeait plus d'un tiers de l'écran : elle
  // défile avec la page au lieu de rester collée.
  const banner = location.pathname !== '/subscribe' && (
    <Alert
      severity="success"
      icon={<WhatsAppIcon sx={{ fontSize: { xs: 26, sm: 32 } }} />}
      action={
        <Button
          color="inherit"
          variant="outlined"
          onClick={() => navigate('/subscribe')}
          sx={{ fontWeight: 700, fontSize: { xs: '0.9rem', sm: '1rem' }, borderColor: 'currentcolor', whiteSpace: 'nowrap' }}
        >
          S'inscrire
        </Button>
      }
      sx={{
        borderRadius: 0,
        bgcolor: '#e7f7ec',
        py: { xs: 1, sm: 2 },
        px: { xs: 2, sm: 3 },
        alignItems: 'center',
        '& .MuiAlert-message': { fontSize: { xs: '0.95rem', sm: '1.15rem' }, fontWeight: 600 },
        '& .MuiAlert-action': { alignItems: 'center', pt: 0 },
      }}
    >
      Recevez chaque jour un proverbe Duala directement sur WhatsApp — inscription gratuite !
    </Alert>
  );

  return (
    <Box ref={rootRef} sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      <Box ref={stickyRef} sx={{ position: 'sticky', top: 0, zIndex: (theme) => theme.zIndex.appBar }}>
        <AppBar position="static" color="primary" elevation={1}>
          <Toolbar variant={isMobile ? 'dense' : 'regular'} sx={{ gap: 1.5 }}>
            <Box
              component="img"
              src="/logo192.png"
              alt="Éphéméride Duala"
              sx={{ height: { xs: 32, sm: 40 }, width: { xs: 32, sm: 40 }, borderRadius: 1 }}
            />
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 700 }}>
              Éphéméride Duala
            </Typography>
            {canGoToAdmin && (
              isMobile
                ? <IconButton color="inherit" onClick={() => navigate('/dashboard')} aria-label="Retour à l'administration"><AdminIcon /></IconButton>
                : <Button color="inherit" variant="outlined" startIcon={<AdminIcon />} onClick={() => navigate('/dashboard')} sx={{ textTransform: 'none', fontWeight: 700, borderColor: 'rgba(255,255,255,0.6)' }}>Retour à l'administration</Button>
            )}
          </Toolbar>
          <Tabs
            value={activeTab}
            onChange={(_, path) => navigate(path)}
            textColor="inherit"
            indicatorColor="secondary"
            variant="fullWidth"
            sx={{ bgcolor: 'primary.dark' }}
          >
            {TABS.map(tab => (
              <Tab
                key={tab.path}
                value={tab.path}
                label={isMobile ? undefined : tab.label}
                aria-label={isMobile ? tab.label : undefined}
                icon={tab.icon}
                iconPosition="start"
                sx={isMobile ? { minWidth: 0, px: 1 } : undefined}
              />
            ))}
          </Tabs>
        </AppBar>

        {!isMobile && banner}
      </Box>

      {isMobile && banner}
      <InstallPrompt />

      <Container component="main" maxWidth="md" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1.5, sm: 3 } }}>
        <Outlet />
        <SponsorBand />
      </Container>

      <SiteFooter />
    </Box>
  );
}

export default PublicPortalLayout;
